import {Action, Card, DataStatus} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {ReactNode, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {type CustomerResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';

import {buildArray, composePath, Nullish, TestIdProps} from 'shared/web';

import {useEditCustomer} from '../../hooks/useEditCustomer';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {CustomerInformationAttributes} from '../CustomerInformationAttributes/CustomerInformationAttributes';

interface CustomerInformationAttributesGuardProps extends TestIdProps {
  customer: CustomerResponseBodyV2 | Nullish;
  resourceId?: string;
  recordId?: string;
}

function CustomerInformationAttributesGuard(props: CustomerInformationAttributesGuardProps) {
  if (isNil(props.customer)) {
    return null;
  }

  return (
    <CustomerInformationAttributes
      customer={props.customer}
      resourceId={props.resourceId}
      recordId={props.recordId}
      data-testid={props['data-testid']}
    />
  );
}

interface CustomerInformationCardProps extends TestIdProps {
  customerId: string;
  isDetailLinkVisible?: boolean;
  onChange?: VoidFunction;
  isEditingDisabled?: boolean;
  isCustomerChangeDisabled?: boolean;
  isChangeCustomerForbidden?: boolean;
  shouldWarnBeforeActiveBusinessCaseEdit?: boolean;
  resourceId?: string;
  recordId?: string;
  headerAction?: ReactNode;
}

export function CustomerInformationCard(props: CustomerInformationCardProps) {
  const [isEditCustomerLoading, setIsEditCustomerLoading] = useState(false);
  const {data, isLoading, isError} = useGetCustomerV2Query({customerId: props.customerId});
  const [canEditCustomer] = usePermissions({permissionKeys: ['editCustomer']});
  const isEditingDisabled = !canEditCustomer || props.isEditingDisabled;

  const editCustomer = useEditCustomer(data, undefined, {
    shouldWarnBeforeActiveBusinessCaseEdit: props.shouldWarnBeforeActiveBusinessCaseEdit,
  });

  const handleEditCustomer = async () => {
    if (!editCustomer) {
      return;
    }

    setIsEditCustomerLoading(true);
    try {
      await editCustomer();
    } finally {
      setIsEditCustomerLoading(false);
    }
  };

  const actions = buildArray<Action>()
    .when(isNotNil(props.headerAction), {
      type: 'custom',
      component: props.headerAction,
    })
    .when(props.isDetailLinkVisible, {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.labels.detail'),
      leftIcon: 'action/open_in_new',
      isDisabled: isNil(data),
      to: composePath(customerRoutes.detail, {
        params: {id: props.customerId},
        isSearchParamsPreserved: false,
      }),
    })
    .when(isNotNil(props.onChange), {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.change'),
      leftIcon: 'navigation/refresh',
      isDisabled: isNil(data) || props.isCustomerChangeDisabled || props.isChangeCustomerForbidden,
      onClick: props.onChange,
    })
    .add({
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.edit'),
      leftIcon: 'editor/mode',
      isDisabled:
        isNil(data) ||
        isEditingDisabled ||
        props.isChangeCustomerForbidden ||
        props.isCustomerChangeDisabled,
      isLoading: isEditCustomerLoading,
      tooltip: isEditingDisabled ? i18n.t('general.labels.noPermission') : undefined,
      onClick: handleEditCustomer,
      'data-testid': props['data-testid'],
    });

  return (
    <Card
      title={i18n.t('entity.customer.labels.customerInformation')}
      actions={actions}
      data-testid={props['data-testid']}
    >
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        minHeight={90}
        data-testid={props['data-testid']}
      >
        <VStack spacing={2}>
          <Show when={data}>
            <CustomerInformationAttributesGuard
              customer={data}
              resourceId={props.resourceId}
              recordId={props.recordId}
              data-testid={props['data-testid']}
            />
          </Show>
        </VStack>
      </DataStatus>
    </Card>
  );
}
